import { ChevronLeft, ChevronRight, Columns3, RectangleVertical } from 'lucide-react'
import { ScheduleFreshnessBadge } from '../../../entities/schedule'
import type { CalendarView } from '../model/calendarView'
import { toIsoDate } from '../model/useWorkerCalendarDays'

interface Props {
  days: Date[]
  /** One column or three. Decides the toggle's pressed segment and the arrows' wording. */
  view?: CalendarView
  /**
   * Omit and no toggle renders — the header is also the planning overlay's, which is always three
   * days and has nothing to switch.
   */
  onViewChange?: (view: CalendarView) => void
  /** Today, so `Heute` can go quiet when the window already contains it. */
  todayIso: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  /** Freshness of the data below. Omit the whole group and no badge renders. */
  lastUpdatedAt?: number | null
  isSyncing?: boolean
  isDisconnected?: boolean
  onRefresh?: () => void
}

/**
 * Nav row, sized to survive a 320px viewport.
 *
 * Controls only, and they never shrink or wrap — a `Heute` that has folded onto a second line costs
 * more height than anything it made room for. There is deliberately no date label here: the
 * three-day columns each carry their own date, and the one-day view names its day in a strip of
 * its own. A range label used to fill the leftover width and truncated to `Mon…` the moment the
 * view toggle joined the row.
 *
 * The budget at 320px, with everything present: 16 padding + 56 `Heute` + 88 grouped arrows + 78
 * view toggle + 44 refresh + ~32 gaps = 314px. Grouping the two arrows into one bordered control
 * rather than leaving them as separate 44px buttons with a gap between them is what pays for the
 * refresh button, and the toggle is icon-only for the same reason.
 *
 * Targets are 44px rather than the 32 they started at. `MIN_TAP_PX` is the floor the blocks on the
 * grid below keep for a thumb in a work glove; these are the controls a Handwerker hits most often,
 * so they get the full recommended size rather than the floor.
 */
export const CalendarHeader = ({
  days,
  view = 'three',
  onViewChange,
  todayIso,
  onPrev,
  onNext,
  onToday,
  lastUpdatedAt = null,
  isSyncing = false,
  isDisconnected = false,
  onRefresh,
}: Props) => {
  const isDay = view === 'day'
  // Nothing to go back to when today is already on screen — and the refresh button now owns the
  // job of re-fetching, which `Heute` used to be the only way to trigger.
  const showsToday = days.some((d) => toIsoDate(d) === todayIso)

  return (
    <div className="flex shrink-0 items-center gap-2 px-2 py-1 sm:px-3 sm:py-2 bg-white border-b border-gray-200 relative z-30">
      <button
        type="button"
        onClick={onToday}
        disabled={showsToday}
        className="shrink-0 h-11 px-2 sm:px-3 rounded-lg text-base font-semibold text-[#124093] hover:bg-gray-100 active:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
      >
        Heute
      </button>

      {/* One control, not two buttons that happen to be adjacent. */}
      <div className="flex shrink-0 items-center rounded-lg border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={onPrev}
          aria-label={isDay ? 'Vorheriger Tag' : 'Vorherige Tage'}
          className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 active:bg-gray-100"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </button>
        <span className="w-px h-6 bg-gray-200" aria-hidden />
        <button
          type="button"
          onClick={onNext}
          aria-label={isDay ? 'Nächster Tag' : 'Nächste Tage'}
          className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-100 active:bg-gray-100"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </button>
      </div>

      {/* Takes the width the date label used to, so the controls stay where they were. */}
      <span className="min-w-0 flex-1" aria-hidden />

      {onViewChange && (
        <div
          role="group"
          aria-label="Ansicht"
          className="flex shrink-0 items-center rounded-lg border border-gray-200 overflow-hidden"
        >
          <ViewButton
            label="Ein Tag"
            pressed={isDay}
            onClick={() => onViewChange('day')}
            icon={<RectangleVertical className="h-5 w-5" strokeWidth={2.25} aria-hidden />}
          />
          <span className="w-px h-6 bg-gray-200" aria-hidden />
          <ViewButton
            label="Drei Tage"
            pressed={!isDay}
            onClick={() => onViewChange('three')}
            icon={<Columns3 className="h-5 w-5" strokeWidth={2.25} aria-hidden />}
          />
        </div>
      )}

      {onRefresh && (
        <ScheduleFreshnessBadge
          lastUpdatedAt={lastUpdatedAt}
          isFetching={isSyncing}
          isDisconnected={isDisconnected}
          onRefresh={onRefresh}
        />
      )}
    </div>
  )
}

interface ViewButtonProps {
  label: string
  pressed: boolean
  onClick: () => void
  icon: React.ReactNode
}

/** Icon-only: a word per segment would not survive 375px beside `Heute`, the arrows and refresh. */
const ViewButton = ({ label, pressed, onClick, icon }: ViewButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={pressed}
    aria-label={label}
    title={label}
    className={`w-[38px] h-11 flex items-center justify-center transition-colors ${
      pressed ? 'bg-bg text-[#124093]' : 'text-gray-500 hover:bg-gray-100 active:bg-gray-100'
    }`}
  >
    {icon}
  </button>
)
